import React from "react";
import { styled } from "@mui/material/styles";
import { Box, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import TvIcon from "@mui/icons-material/Tv";
import SmartphoneIcon from "@mui/icons-material/Smartphone";
import googleTvLogo from "../../assets/google-tv-logo-dark.png";
import appleTvLogo from "../../assets/apple-tv-logo-white.png";

// #894: "Watch on your TV" promo, shared by /app and /link-changed. Copy
// is from app-tv/docs/supported-devices/get-farmflix-on-your-tv.md. Fire TV
// keeps the logo image already used on /bigscreen. Google TV uses Google's
// dark-background logo and Apple TV the outline Apple TV logo (recoloured
// white for the dark tile), both added at Ellaie's request without Google's
// or Apple's brand approval, which their rules ask for.
//
// Every text and icon colour below is named explicitly from the theme:
// MUI's defaults assume a light background (CLAUDE.md, "Styling").

const FIRETV_LOGO_URL =
  "https://farmflix-images-prod.s3.eu-west-1.amazonaws.com/firetv_logo.png";

type Tile = {
  key: string;
  heading: string;
  description: string;
  action: string;
  logo?: string;
  logoAlt?: string;
};

const tiles: Tile[] = [
  {
    key: "firetv",
    heading: "Amazon Fire TV",
    description: "Fire TV Stick, Fire TV Cube, or a TV with Fire TV built in.",
    action: "Press Home, search FarmFlix and install.",
    logo: FIRETV_LOGO_URL,
  },
  {
    key: "googletv",
    heading: "Google TV and Android TV",
    description:
      "Smart TVs from Sony, TCL, Philips and others, and Google TV sticks.",
    action: "Press Home, search FarmFlix and install.",
    logo: googleTvLogo,
    logoAlt: "Google TV logo",
  },
  {
    key: "appletv",
    heading: "Apple TV",
    description: "The small black Apple box.",
    action: "Open the App Store, search FarmFlix and install.",
    logo: appleTvLogo,
    logoAlt: "Apple TV logo",
  },
];

const Section = styled(Box)(({ theme }) => ({
  width: "100%",
  boxSizing: "border-box",
  backgroundColor: theme.palette.background.ffNavyLight,
  borderRadius: 16,
  padding: theme.spacing(4, 3),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(3),
}));

const TileGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
  gap: theme.spacing(2),
}));

const TileBox = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.background.ffNavyLighter,
  borderRadius: 12,
  padding: theme.spacing(2.5),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1),
}));

const SignInNote = styled(Box)(({ theme }) => ({
  display: "flex",
  gap: theme.spacing(1.75),
  alignItems: "flex-start",
  backgroundColor: theme.palette.background.ffNavy,
  borderRadius: 12,
  padding: theme.spacing(2, 2.5),
}));

const GreenLink = styled(Link)(({ theme }) => ({
  color: theme.palette.background.ffGreen,
  fontWeight: 500,
}));

const TvAppPromo = () => (
  <Section>
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 1,
        textAlign: "center",
      }}
    >
      <TvIcon sx={{ color: "background.ffGreen", fontSize: 40 }} />
      <Typography
        variant="h2"
        sx={{ color: "common.white", fontSize: "1.75rem", fontWeight: 700 }}
      >
        Watch on your TV
      </Typography>
      <Typography
        sx={{ color: "common.darkgray", fontSize: "1.05rem", maxWidth: 560 }}
      >
        The FarmFlix TV app is free to download and included in your membership.
      </Typography>
    </Box>

    <TileGrid>
      {tiles.map((tile) => (
        <TileBox key={tile.key}>
          <Box sx={{ height: 40, display: "flex", alignItems: "center" }}>
            {tile.logo && (
              <img
                src={tile.logo}
                alt={tile.logoAlt ?? `${tile.heading} logo`}
                style={{ maxHeight: "100%", maxWidth: 140 }}
              />
            )}
          </Box>
          <Typography
            variant="h3"
            sx={{ color: "common.white", fontSize: "1.15rem", fontWeight: 500 }}
          >
            {tile.heading}
          </Typography>
          <Typography sx={{ color: "common.darkgray", fontSize: "0.95rem" }}>
            {tile.description}
          </Typography>
          <Typography sx={{ color: "common.white", fontSize: "0.95rem" }}>
            {tile.action}
          </Typography>
        </TileBox>
      ))}
    </TileGrid>

    <SignInNote>
      <SmartphoneIcon
        sx={{ color: "background.ffGreen", fontSize: 28, flexShrink: 0 }}
      />
      <Typography sx={{ color: "common.white", fontSize: "0.95rem" }}>
        Signing in is simple. The TV shows a short code, you open the FarmFlix
        app on your phone, and it signs you in. There is no need to type your
        password with the remote.
      </Typography>
    </SignInNote>

    <Typography
      sx={{ color: "common.white", textAlign: "center", fontSize: "1rem" }}
    >
      Not sure what you have?{" "}
      <GreenLink to="/bigscreen">See which TVs work</GreenLink>
    </Typography>
  </Section>
);

export default TvAppPromo;
